/**
 * CR403/CR404: Add new category modal
 * CR404: Renamed "Slug" to "Category Key"
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface AddCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (params: { name: string; slug?: string }) => Promise<unknown>;
  isCreating?: boolean;
}

export function AddCategoryModal({
  open,
  onOpenChange,
  onCreate,
  isCreating,
}: AddCategoryModalProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) return;

    await onCreate({
      name: name.trim(),
      slug: slug.trim() || undefined,
    });

    // Reset and close
    setName("");
    setSlug("");
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setName("");
      setSlug("");
    }
    onOpenChange(newOpen);
  };

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (!slug) {
      // Auto-generate slug when user hasn't manually set one
      // But don't overwrite if they've typed something
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Category</DialogTitle>
          <DialogDescription>
            Create a new category for organizing merchants
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="new_cat_name">Name *</Label>
            <Input
              id="new_cat_name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Electronics"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_cat_slug">Category Key (optional)</Label>
            <Input
              id="new_cat_slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Auto-generated from name"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to auto-generate from name
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Create Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
